import { HotelCardSkeleton } from '@lt/components';
import React, { useEffect, useRef, useState } from 'react';
import { Popup } from 'react-mapbox-gl';
import styled, { css } from 'styled-components';

import { pushMapHotelCardPopup } from 'src/components/Mapbox/analitics';

import HotelRenderer from 'src/routes/SearchPage/containers/HotelRenderer';
import useLockedBody from 'src/hooks/useLockedBody';
import { useSwipePopup } from 'src/hooks/useSwipePopup';
import { useOnClickEventsOutside } from 'src/hooks/useOnClickEventsOutside';
import { POPUP_CLASS } from '../../constants';

import type { PopupState } from '../../../../store/map/types';
import {
  desktopClosePopupAnimation,
  desktopShowPopupAnimation,
  mobileClosePopupAnimation,
  mobileShowPopupAnimation,
} from './styles';

interface HotelPopupProps {
  popupData?: PopupState;
  isMobile: boolean;
  resetPopupDataHandler: () => void;
  isExpandedMap?: boolean;
  isExpandableMap?: boolean;
}

const ANIMATION_TIMEOUT_DELAY = 300;

/**
 * Попап с выбранным отелем
 */
export const HotelPopupv2 = (props: HotelPopupProps) => {
  const {
    isMobile,
    popupData,
    resetPopupDataHandler,
    isExpandedMap,
    isExpandableMap,
  } = props;
  const [popupStates, setPopupStates] = useState<(PopupState | undefined)[]>(
    [],
  );
  const [animationState, setAnimationState] = useState<string>('close');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [shownData, setShownData] = useState<PopupState | undefined>();

  useEffect(() => {
    if (popupData) {
      pushMapHotelCardPopup(isMobile, popupData.state);
    }
    setPopupStates((prevStates) => [...prevStates, popupData]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupData]);

  useEffect(() => {
    // периодически очищаем предыдущие состояния попапа
    if (popupStates.length >= 4) {
      setPopupStates((prevStates) => [
        ...prevStates.slice(prevStates.length - 2),
      ]);
    }

    // работаем с последним и предпоследним состояниями попапа (закрыт-открыт или открыт-закрыт)
    const lastState = popupStates[popupStates.length - 1];
    const prevState = popupStates[popupStates.length - 2];

    if (!lastState && !prevState) {
      return;
    }

    // если предыдущее состояние undefined, а последнее - не undefined, значит попап переходит из закрытого состояния в открытое
    if (!prevState && lastState && !isOpen) {
      // анимация открытия попапа
      setShownData(lastState);
      setAnimationState('open');

      // если предыдущее состояние не undefined, а последнее - undefined, значит попап переходит из открытого состояния в закрытое
    } else if (prevState && !lastState && isOpen) {
      // анимация закрытия попапа
      setAnimationState('close');
    }
  }, [popupStates, isOpen]);

  // ставим overflow: hidden для body, когда попап открыт
  const [, setLocked] = useLockedBody(false);
  const popupRef = useRef(null);

  const { delta } = useSwipePopup({
    ref: popupRef,
    isReset: isOpen,
    onSwipe: resetPopupDataHandler,
  });

  useEffect(() => {
    if (isMobile) {
      setLocked(isOpen);
    }
  }, [isOpen, isMobile, setLocked]);

  useOnClickEventsOutside({
    ref: popupRef,
    isMobile,
    callback: resetPopupDataHandler,
  });

  const handleAnimationEnd = () => {
    if (animationState === 'open') {
      setIsOpen(true);
    } else {
      setShownData(undefined);
      setIsOpen(false);
    }
  };

  if (!shownData) {
    return null;
  }

  return (
    <PopupWrapper
      $isExpandedMap={isExpandedMap}
      onAnimationEnd={handleAnimationEnd}
      $isExpandableMap={isExpandableMap}
      $isMobile={isMobile}
      $opened={animationState === 'open'}
      $delta={delta}
    >
      <StyledPopup
        className={shownData.hotelInfo ? POPUP_CLASS : ''}
        coordinates={shownData.coordinates}
        anchor={isMobile ? 'top' : undefined}
      >
        <div ref={popupRef}>
          {shownData.hotelInfo ? (
            <HotelRenderer
              isMapCard
              isMobile={isMobile}
              hotelInfo={shownData.hotelInfo}
              defaultImages={shownData.hotelInfo.hotel.images}
            />
          ) : (
            <HotelCardSkeleton small />
          )}
        </div>
      </StyledPopup>
    </PopupWrapper>
  );
};

const PopupWrapper = styled.div<{
  $isExpandedMap?: boolean;
  $isExpandableMap?: boolean;
  $isMapCard?: boolean;
  $isMobile?: boolean;
  $opened: boolean;
  $delta: number;
}>`
  z-index: 11000 !important;
  position: absolute;
  ${({ $isMobile, $isExpandedMap, $isExpandableMap, $opened, $delta }) =>
    $isMobile
      ? css`
          transform: none !important;
          top: auto;
          left: 0;
          right: 0;
          bottom: calc(0px - ${$delta}px);
          position: fixed !important;
          animation: ${$opened
              ? mobileShowPopupAnimation
              : mobileClosePopupAnimation($delta)}
            ${ANIMATION_TIMEOUT_DELAY * 0.001}s linear;
        `
      : css`
          top: auto;
          right: 15px;
          bottom: 30px;
          left: auto;
          justify-content: end;
          max-width: 650px;
          width: 100%;
          transform: scale(0.8) !important;
          transform-origin: bottom right;
          animation: ${$opened
              ? desktopShowPopupAnimation
              : desktopClosePopupAnimation}
            ${ANIMATION_TIMEOUT_DELAY * 0.001}s linear;

          ${!$isExpandedMap &&
          $isExpandableMap &&
          css`
            width: calc(125% - 30px);
          `}
        `}
`;

const StyledPopup = styled(Popup)<{
  $isMobile?: boolean;
}>`
  transform: none !important;
  position: static;
  & .mapboxgl-popup-tip {
    display: none !important;
  }

  .mapboxgl-popup-content {
    padding: 0;
    border-radius: ${({ $isMobile }) => ($isMobile ? '8px 8px 0 0' : '8px')};
    overflow: hidden;
    /* для корректной работы border-radius и overflow в Safari */
    isolation: isolate;
  }
`;
